import { cloneElement, isValidElement, useContext, useState } from 'react'
import { Sparkles } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { AiChatConversationContent } from './ai-chat-conversation-content'
import { AiChatConversationControls } from './ai-chat-conversation-controls'
import { AiChatPanelContext } from './ai-chat-panel-context'
import {
  documentCollectionPrompts,
  workspacePrompts,
} from './ai-chat-live-content'
import { useAiChatLiveState } from './ai-chat-live-state'
import type { LiveAiChatScope } from './ai-chat-live-content'
import type { CurrentUserQuery } from '@/graphql/queries/currentUserQuery.gql.generated'
import { canCurrentUserPerform } from '@/shared/lib/workspace-permissions'
import { Button } from '@/shared/ui/button'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/shared/ui/sheet'

type CurrentUser = CurrentUserQuery['currentUser']

export function canOpenAiChat(currentUser: CurrentUser | null | undefined) {
  if (!currentUser) {
    return false
  }

  if ('can' in currentUser && typeof currentUser.can === 'function') {
    return currentUser.can('access', 'mcp')
  }

  return canCurrentUserPerform(currentUser, 'access', 'mcp')
}

export function AiChatEntryButton({
  documentCollectionId,
  documentCollectionName,
  scope,
  trigger,
  workspaceId,
}: {
  documentCollectionId?: string
  documentCollectionName?: string
  scope: LiveAiChatScope
  trigger?: React.ReactNode
  workspaceId?: string
}) {
  const panel = useContext(AiChatPanelContext)
  const [open, setOpen] = useState(false)
  const { t } = useTranslation('aiChat')
  const label =
    scope === 'workspace' ? t('aiChat:askAi') : t('aiChat:askAiAboutTable')

  if (panel) {
    return trigger ? (
      <span className="contents" onClick={panel.openChat}>
        {isValidElement<{ onSelect?: () => void }>(trigger)
          ? cloneElement(trigger, { onSelect: undefined })
          : trigger}
      </span>
    ) : (
      <Button type="button" variant="outline" onClick={panel.openChat}>
        <Sparkles className="size-4" />
        {label}
      </Button>
    )
  }

  return (
    <>
      {trigger ? null : (
        <Button type="button" variant="outline" onClick={() => setOpen(true)}>
          <Sparkles className="size-4" />
          {label}
        </Button>
      )}
      <AiChatLiveDrawer
        key={`${workspaceId ?? ''}:${scope}:${documentCollectionId ?? ''}`}
        documentCollectionId={documentCollectionId}
        documentCollectionName={documentCollectionName}
        open={open}
        scope={scope}
        trigger={trigger}
        workspaceId={workspaceId}
        onOpenChange={setOpen}
      />
    </>
  )
}

export function getDrawerCopy({
  documentCollectionName,
  scope,
  t,
}: {
  documentCollectionName?: string
  scope: LiveAiChatScope
  t: ReturnType<typeof useTranslation<'aiChat'>>['t']
}) {
  if (scope === 'workspace') {
    return {
      prompts: workspacePrompts,
      subtitle: t('aiChat:workspaceSubtitle'),
      title: t('aiChat:workspaceTitle'),
    }
  }

  return {
    prompts: documentCollectionPrompts,
    subtitle: t('aiChat:collectionSubtitle'),
    title: t('aiChat:collectionTitle', {
      name: documentCollectionName ?? t('aiChat:collectionFallbackName'),
    }),
  }
}

function AiChatLiveDrawer({
  documentCollectionId,
  documentCollectionName,
  open,
  scope,
  trigger,
  workspaceId,
  onOpenChange,
}: {
  documentCollectionId?: string
  documentCollectionName?: string
  open: boolean
  scope: LiveAiChatScope
  trigger?: React.ReactNode
  workspaceId?: string
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation('aiChat')
  const copy = getDrawerCopy({ documentCollectionName, scope, t })
  const state = useAiChatLiveState({
    documentCollectionId,
    open,
    scope,
    workspaceId,
  })

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      {trigger ? <SheetTrigger asChild>{trigger}</SheetTrigger> : null}
      <AiChatLiveDrawerSheetContent copy={copy} scope={scope} state={state} />
    </Sheet>
  )
}

function AiChatLiveDrawerSheetContent({
  copy,
  scope,
  state,
}: {
  copy: ReturnType<typeof getDrawerCopy>
  scope: LiveAiChatScope
  state: ReturnType<typeof useAiChatLiveState>
}) {
  const { t } = useTranslation('aiChat')

  return (
    <SheetContent side="right" className="w-full gap-0 p-0 sm:max-w-2xl">
      <SheetHeader className="border-b p-5">
        <div className="flex items-center justify-between gap-3 pr-8">
          <div className="flex min-w-0 items-center gap-2">
            <SheetTitle>
              {state.sessionTitle ?? t('aiChat:newConversation')}
            </SheetTitle>
          </div>
        </div>
        <SheetDescription>{copy.title}</SheetDescription>
        <AiChatConversationControls
          disabled={state.blocked}
          historyOpen={state.historyOpen}
          onNewConversation={state.newConversation}
          onHistory={state.openHistory}
          onBack={state.closeHistory}
        />
      </SheetHeader>

      <AiChatConversationContent
        state={state}
        prompts={copy.prompts}
        scope={scope}
      />
    </SheetContent>
  )
}
